<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomButton,
  AtomIcon,
  AtomDrawer,
  useDate,
} from "@chatfood/design-system";
import { ref, computed } from "vue";
import { OrderingDetails } from "~/components/ordering-details";
import { I18nTranslate } from "~/components/i18n-translate";
import { useOutletStore } from "~/stores/outlet";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import { useWindowResize } from "~/composables/use-window-resize";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import { useDomainLabel } from "~/composables/use-domain-label";

const css = bemBuilder("ordering-summary-button");
const { t } = useLocale();
const { getReadableDate, formatTime } = useDate();
const { orderingModeMap } = useDomainLabel();
const { isTablet } = useWindowResize();
const { route } = useNavigation();

const basket = useBasketStore();
const customer = useCustomerStore();
const outlet = useOutletStore();

const showMenu = ref<boolean>(false);

const toggleShowMenu = () => {
  showMenu.value = !showMenu.value;
};

const isVisible = computed(() => {
  return route.value.name === "outlet" || route.value.name === "menu";
});

const scheduledDay = computed(() => {
  if (!basket.scheduledTime) return;
  return getReadableDate(basket.scheduledTime.day, "ll");
});

const scheduledTime = computed(() => {
  if (!basket.scheduledTime) return;
  return formatTime(basket.scheduledTime.time);
});

const customerAddressLabel = computed(() => {
  const address = customer.addresses.find(
    (address) => address.id === basket.addressId,
  );

  return address?.nickname;
});

const adjustedEta = computed(() => {
  const eta = basket.isForDelivery
    ? outlet.orderingMode.estimatedCompletionMinutes
    : outlet.orderingMode.preparationMinutes;

  if (eta === 0) return;

  return eta + ETA_PREPARATION_MARGIN;
});
</script>

<template>
  <div v-if="isVisible" :class="css('')">
    <AtomButton
      variant="secondary"
      theme="tailored"
      :class="css('button')"
      wide
      @click="toggleShowMenu"
    >
      <div :class="css('wrapper')">
        <template v-if="basket.isForPickup && basket.scheduledTime">
          <I18nTranslate
            data-test="summary-label-pickup-scheduled"
            keypath="components.ordering_summary_button.pickup_scheduled"
          >
            <template #mode>
              <strong>{{ orderingModeMap["pickup"] }}</strong>
            </template>
            <template #target>
              <strong>{{ outlet.name }}</strong>
            </template>
            <template #day>
              <strong>{{ scheduledDay }}</strong>
            </template>
            <template #time>
              <strong>{{ scheduledTime }}</strong>
            </template>
          </I18nTranslate>
        </template>
        <template v-else-if="basket.isForPickup">
          <I18nTranslate
            data-test="summary-label-pickup-eta"
            keypath="components.ordering_summary_button.pickup_eta"
          >
            <template #mode>
              <strong>{{ orderingModeMap["pickup"] }}</strong>
            </template>
            <template #target>
              <strong>{{ outlet.name }}</strong>
            </template>
            <template v-if="adjustedEta" #eta>
              <strong>{{
                t("components.ordering_summary_button.eta_in_min", {
                  minutes: adjustedEta,
                })
              }}</strong>
            </template>
          </I18nTranslate>
        </template>
        <template v-else-if="basket.isForCurbside && basket.scheduledTime">
          <I18nTranslate
            data-test="summary-label-curbside-scheduled"
            keypath="components.ordering_summary_button.curbside_scheduled"
          >
            <template #mode>
              <strong>{{ orderingModeMap["curbside"] }}</strong>
            </template>
            <template #target>
              <strong>{{ outlet.name }}</strong>
            </template>
            <template #day>
              <strong>{{ scheduledDay }}</strong>
            </template>
            <template #time>
              <strong>{{ scheduledTime }}</strong>
            </template>
          </I18nTranslate>
        </template>
        <template v-else-if="basket.isForCurbside">
          <I18nTranslate
            data-test="summary-label-curbside-eta"
            keypath="components.ordering_summary_button.curbside_eta"
          >
            <template #mode>
              <strong>{{ orderingModeMap["curbside"] }}</strong>
            </template>
            <template #target>
              <strong>{{ outlet.name }}</strong>
            </template>
            <template v-if="adjustedEta" #eta>
              <strong>{{
                t("components.ordering_summary_button.eta_in_min", {
                  minutes: adjustedEta,
                })
              }}</strong>
            </template>
          </I18nTranslate>
        </template>
        <template v-else-if="!basket.currentLocation && !basket.addressId">
          <I18nTranslate
            data-test="summary-label-delivery-etaless"
            keypath="components.ordering_summary_button.delivery_etaless"
          >
            <template #mode>
              <strong>{{ orderingModeMap["delivery"] }}</strong>
            </template>
            <template #target>
              <strong>
                {{ t("components.ordering_summary_button.undefined_location") }}
              </strong>
            </template>
          </I18nTranslate>
        </template>
        <template v-else-if="basket.scheduledTime">
          <I18nTranslate
            data-test="summary-label-delivery-scheduled"
            keypath="components.ordering_summary_button.delivery_scheduled"
          >
            <template #mode>
              <strong>{{ orderingModeMap["delivery"] }}</strong>
            </template>
            <template #target>
              <strong>{{
                customerAddressLabel ??
                t("components.ordering_summary_button.current_location")
              }}</strong>
            </template>
            <template #day>
              <strong>{{ scheduledDay }}</strong>
            </template>
            <template #time>
              <strong>{{ scheduledTime }}</strong>
            </template>
          </I18nTranslate>
        </template>
        <template v-else>
          <I18nTranslate
            data-test="summary-label-delivery-eta"
            keypath="components.ordering_summary_button.delivery_eta"
          >
            <template #mode>
              <strong>{{ orderingModeMap["delivery"] }}</strong>
            </template>
            <template #target>
              <strong>{{
                customerAddressLabel ??
                t("components.ordering_summary_button.current_location")
              }}</strong>
            </template>
            <template v-if="adjustedEta" #eta>
              <strong>{{
                t("components.ordering_summary_button.eta_in_min", {
                  minutes: adjustedEta,
                })
              }}</strong>
            </template>
          </I18nTranslate>
        </template>
      </div>
      <AtomIcon
        name="caret-right"
        ignore-color
        size="small"
        :class="[
          css('caret-icon'),
          { [css('caret-icon', 'clicked')]: showMenu },
        ]"
      />
    </AtomButton>
    <AtomDrawer
      v-if="isTablet"
      v-model="showMenu"
      :class="css('content-mobile')"
    >
      <OrderingDetails
        :show-menu="showMenu"
        @toggle-show-menu="toggleShowMenu"
      />
    </AtomDrawer>
    <OrderingDetails
      v-else
      as-dropdown
      :show-menu="showMenu"
      @toggle-show-menu="toggleShowMenu"
    />
  </div>
</template>

<style lang="scss" scoped>
.ordering-summary-button {
  display: flex;
  flex-direction: column;
  position: relative;

  &__button {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-md);
    text-align: left;
  }

  &__wrapper {
    font-weight: normal;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-height: 1.5;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  &__caret-icon {
    rotate: 90deg;
    transition: rotate 250ms ease-in-out;

    &--clicked {
      rotate: -90deg;
    }
  }
}
</style>
